import {
  Component,
  Expressions,
  Types,
  matchExpression,
  Plugins,
} from '@packages/systems/wfdl';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  getPluginName,
  namesEqual,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {identity, constant} from '@packages/utilities/fp/utils';
import type {NodePojo} from '@packages/systems/core/nodepojo';
import type {EvalElementProps} from '@packages/systems/render';

type ElementContent = {text: string; existingId: string | false};
const EMPTY_ELEMENT_CONTENT: Readonly<ElementContent> = {
  text: '',
  existingId: false,
};
const NON_BREAKING_SPACE_REGEXP = new RegExp(' ', 'g');
const stringType = qualify(Plugins.name('Basic'), Component.name('String'));
const lineBreakType = qualify(
  Plugins.name('Basic'),
  Component.name('LineBreak')
);
const constantEmptyString = constant('');
const constantEmptyElementContent = constant(EMPTY_ELEMENT_CONTENT);

const concatenateTextFromExpressions = (list: Expression[]) =>
  list
    .reduce((acc, curr) => acc + getStringFromExpression(curr), '')
    .replace(NON_BREAKING_SPACE_REGEXP, ' ');

const getStringFromStringElement = matchExpression({
  Text: identity,
  _: constantEmptyString,
});

const getStringFromChildren: (arg1: Expression) => string =
  matchExpression<string>({
    Record: (fields) => {
      if (fields.hasOwnProperty('children')) {
        return getStringFromListOfElements(fields.children);
      }
      return '';
    },
    _: constantEmptyString,
  });

export const getStringFromElement = (el: ExpressionElement) => {
  // Text element
  if (namesEqual(el.type, stringType)) {
    return getStringFromStringElement(el.data);
  }
  // LineBreak element
  else if (namesEqual(el.type, lineBreakType)) {
    return '\n';
  }
  // Children
  return getStringFromChildren(el.data);
};

const getStringFromExpression: (arg1: Expression) => string =
  matchExpression<string>({
    Element: getStringFromElement,
    _: constantEmptyString,
  });

export const getStringFromListOfElements = matchExpression<string>({
  List: concatenateTextFromExpressions,
  _: constantEmptyString,
});

/**
 * An evaluated element flowing through functions such as listOfElementsToText
 * can be in 1 of 3 forms:
 * 1. A plain string (coming from the renderer)
 * 2. A React element instance of EvalComponentElement or EvalAtomElement
 *    (coming from the renderer)
 * 3. An NodePojo (coming from Dynamo and default values)
 */
export type EvalElementType =
  | string
  | React.ReactElement<EvalElementProps>
  | NodePojo;

export const extractTextFromEvalElement = (
  text: string,
  element: EvalElementType
): string => {
  // Option 1: it's a string
  if (typeof element === 'string') {
    return text + element;
  }

  // Option 2: it's an instance of EvalComponentElement or EvalAtomElement
  if ('props' in element) {
    // Extract text from element
    return text + getStringFromElement(element.props.element);
  }

  // Option 3: it's an NodePojo
  if ('id' in element) {
    if (
      element.text &&
      element.data?.value &&
      typeof element.data.value === 'string'
    ) {
      // Concatenate values from text nodes
      return text + element.data.value;
    }
    if (
      element.pluginType === 'Dynamo' &&
      element.type === 'InterimLegacyRichTextValue' &&
      element.data?.value &&
      typeof element.data.value === 'string'
    ) {
      // a legacy HTML string rich text value via Dynamo universal binding
      return text + element.data.value;
    }
    if (element.type === 'LineBreak') {
      // Add a new line
      return text + '\n';
    }
    if (element.children) {
      // Extract text from children
      return element.children.reduce(extractTextFromEvalElement, text);
    }
  }

  return text;
};

const SITE_COMPONENT_PLUGIN_NAME = '__SitePlugin';

export const containsComponentInstance = (
  elements: EvalElementType[]
): boolean =>
  elements.some((element) => {
    if (typeof element === 'string') return false;
    if ('props' in element) {
      return (
        getPluginName(element.props.element.type) === SITE_COMPONENT_PLUGIN_NAME
      );
    }
    if ('id' in element) {
      return (
        (element.type === 'Symbol' && Boolean(element.data?.sym?.inst)) ||
        containsComponentInstance(element.children ?? [])
      );
    }
    return false;
  });

// Duplicated rather than imported from @packages/systems/translation/utils —
// keeps element-content dependency-light; it's 4 lines.
function collectChildrenAndNode(node: NodePojo): NodePojo[] {
  return [node, ...(node.children?.flatMap(collectChildrenAndNode) ?? [])];
}

function textFromOverride(expr: Expression): string | undefined {
  if (Expressions.isList(expr)) return getStringFromListOfElements(expr);
  if (Expressions.isText(expr)) return Expressions.getText(expr) ?? '';
  return undefined; // link/boolean/number/etc. — not a text-holding literal
}

function textFromComponentDefault(
  componentType: Component.ComponentType,
  propKey: string
): string {
  const fields = Types.getRecordFields(Component.getDataType(componentType));
  const fieldType = fields?.[propKey];
  if (!fieldType) return '';

  const defaultValue = Types.getDefaultValue(fieldType, undefined);
  if (typeof defaultValue === 'string') return defaultValue;
  if (Array.isArray(defaultValue) && Types.isListOfElements(fieldType)) {
    return (defaultValue as NodePojo[])
      .flatMap(collectChildrenAndNode)
      .filter((n) => n.text)
      .map((n) => String(n.data?.value ?? ''))
      .join('');
  }
  return '';
}

/**
 * Plain-text snapshot of `instanceOrElement.data` at `key`, taken right
 * before that key gets rebound to a CMS personalization field — used to
 * seed the new field's value.
 *
 * - Instance override present at `key` → read the literal directly.
 * - No override, `componentType` given → fall back to the prop's declared
 *   default on the component's data type (prop-bound path / Hero resolver).
 * - No override, no `componentType` → '' (direct-text paths pass no
 *   component, since eligibility already guarantees a literal is present).
 */
export function getCurrentLiteralText(
  instanceOrElement: ExpressionElement,
  key: Array<{in: 'Record'; at: string}>,
  componentType?: Component.ComponentType
): string {
  const expr =
    Expressions.getIn(instanceOrElement.data, key) ??
    // Legacy instance-override path — mirrors isAlreadyPersonalized's dual
    // lookup so a pre-migration override isn't silently missed.
    Expressions.getIn(instanceOrElement.data, [
      {in: 'Record', at: 'sym'},
      {in: 'Record', at: 'overrides'},
      ...key,
    ]);

  if (expr) {
    const overrideText = textFromOverride(expr);
    if (overrideText !== undefined) return overrideText;
  }

  const propKey = key[key.length - 1]?.at;
  if (componentType && propKey) {
    return textFromComponentDefault(componentType, propKey);
  }
  return '';
}

export const getElementContentFromListOfElements =
  matchExpression<ElementContent>({
    List: (items) => {
      const text = concatenateTextFromExpressions(items);

      if (items.length !== 1) {
        // TODO (Localization) Does this mean multi-line text overrides get out
        //  of sync between locales as we are always regenerating the element IDs?
        return {text, existingId: false};
      }

      const [firstItem] = items;

      if (!firstItem) {
        return {text, existingId: false};
      }

      const existingId = Expressions.getElement(firstItem)?.id ?? false;

      return {text, existingId};
    },
    _: constantEmptyElementContent,
  });
